import { html, css, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { localized, msg } from "@lit/localize";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-button";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-icon";

import type { InformationAndHoursContent } from "../../types/content";
import { WebKioskPageElement } from "../web-kiosk-page";
import {
  fetchStoreHours,
  type StoreHours,
} from "../../services/store-info-service";
import { getStore } from "../../utilities/navigation.utility";
import { getStoreIdFromQueryString } from "../../utilities/store-id-to-payor";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-information-and-hours": WebKioskInformationAndHoursElement;
  }
}
@localized()
@customElement("web-kiosk-information-and-hours")
export class WebKioskInformationAndHoursElement extends WebKioskPageElement {
  @state() content: InformationAndHoursContent | undefined = undefined;

  @state() storeHours: StoreHours | undefined = undefined;

  firstUpdated() {
    const store = getStore(window.location.search);
    const storeId = getStoreIdFromQueryString(store);
    this.storeHours = fetchStoreHours(storeId ?? "");
  }

  renderStoreHours(storeHours?: StoreHours): TemplateResult | typeof nothing {
    if (!storeHours) {
      return nothing;
    }

    return html`
      <div id="hours-details">
        <dl>
          <dd
            aria-label=${`${msg("Monday", { id: "monday" })} ${
              storeHours.mondayHours
            }`}
          >
            <span> ${msg("Monday", { id: "monday" })}</span
            >${storeHours.mondayHours}
          </dd>
          <dd
            aria-label=${`${msg("Tuesday", { id: "tuesday" })} ${
              storeHours.tuesdayHours
            }`}
          >
            <span> ${msg("Tuesday", { id: "tuesday" })}</span
            >${storeHours.tuesdayHours}
          </dd>
          <dd
            aria-label=${`${msg("Wednesday", { id: "wednesday" })} ${
              storeHours.wednesdayHours
            }`}
          >
            <span> ${msg("Wednesday", { id: "wednesday" })}</span
            >${storeHours.wednesdayHours}
          </dd>
          <dd
            aria-label=${`${msg("Thursday", { id: "thursday" })} ${
              storeHours.thursdayHours
            }`}
          >
            <span> ${msg("Thursday", { id: "thursday" })}</span
            >${storeHours.thursdayHours}
          </dd>
        </dl>
        <dl>
          <dd
            aria-label=${`${msg("Friday", { id: "friday" })} ${
              storeHours.fridayHours
            }`}
          >
            <span> ${msg("Friday", { id: "friday" })}</span
            >${storeHours.fridayHours}
          </dd>
          <dd
            aria-label=${`${msg("Saturday", { id: "saturday" })} ${
              storeHours.saturdayHours
            }`}
          >
            <span> ${msg("Saturday", { id: "saturday" })}</span
            >${storeHours.saturdayHours}
          </dd>
          <dd
            aria-label=${`${msg("Sunday", { id: "sunday" })} ${
              storeHours.sundayHours
            }`}
          >
            <span> ${msg("Sunday", { id: "sunday" })}</span
            >${storeHours.sundayHours}
          </dd>
        </dl>
      </div>
    `;
  }

  renderLearnMore(): TemplateResult | typeof nothing {
    const learnMoreContent = this.content?.learnMore;
    if (learnMoreContent === undefined) {
      return nothing;
    }
    return html`<p class="learn-more">
      ${unsafeHTML(this.content?.learnMore)}
    </p>`;
  }

  render(): TemplateResult {
    return html`<section id="info-and-hours">
      <div id="navigation">
        <a href=${this.createRelativeLink("about")}>
          <wh-icon name="chevron-down"></wh-icon>
          ${msg("Back", { id: "info-and-hours-back" })}
        </a>
      </div>
      <div id="left-panel">
        <h1 tabindex="-1">${this.content?.heading}</h1>
        <p>${this.content?.description}</p>
        <h2>${this.content?.hoursTitle}</h2>
        ${this.renderStoreHours(this.storeHours)}
      </div>
      <div id="right-panel">
        <div>
          <div>
            <p>${this.content?.advisorsDescription}</p>
            ${this.renderLearnMore()}
            <p class="small narrow-text">${this.content?.legalText}</p>
          </div>
        </div>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg-blue: #c1e8ff;
      --web-kiosk-bg-grey: #f5f5f0;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
      --web-kiosk-hours-border-color: #3232643d;
      --web-kiosk-panel-padding: 64px;
    }

    h1 {
      margin: 0 0 8px;
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 64px;
      font-weight: 700;
      line-height: 72px;
      margin-right: 80px;
    }

    h2 {
      margin: 0;
      padding: 0;
      color: var(--web-kiosk-text-color, blue);
      font-size: 32px;
      font-weight: 700;
      line-height: 38px;
      padding-bottom: 16px;
    }

    p {
      font-size: 26px;
      padding-bottom: 40px;
      line-height: 40px;
      font-weight: 400;
    }

    p.small {
      font-size: 22px;
      line-height: 32px;
    }

    p.legal {
      font-size: 20px;
      line-height: 28px;
    }

    .narrow-text {
      max-width: 67%;
      width: fit-content;
    }

    .learn-more {
      max-width: 67%;
      width: fit-content;
      line-height: 34px;
      font-weight: 700;
    }

    .no-height-break {
      height: 0;
    }

    strong {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 56px;
    }

    #info-and-hours {
      height: 100%;
      background: url("assets/about/caregiver.svg") right -5% bottom no-repeat,
        url("assets/informationAndHours/swirl.svg") 100% 0 no-repeat,
        url("assets/shared/bg/city-skyline.svg") left 3% bottom -7% no-repeat,
        linear-gradient(
          90deg,
          var(--web-kiosk-bg-grey, #f5f5f0) 50%,
          var(--web-kiosk-bg-blue, #c1e8ff) 50%
        );
      background-size: 23%, 50%, 33%, cover;
    }

    #navigation {
      display: flex;
      padding: 52px 58px 48px 56px;
      color: var(--web-kiosk-link-color, hotpink);
      font-size: 32px;
    }

    #navigation a {
      width: 20%;
      display: flex;
      align-items: center;
      color: var(--web-kiosk-link-color, hotpink);
      text-decoration: none;
      font-weight: bold;
    }

    #navigation wh-icon {
      font-size: 50px;
      transform: rotate(90deg);
    }

    #left-panel {
      float: left;
      width: 50%;
      box-sizing: border-box;
      padding: 0 var(--web-kiosk-panel-padding, 64px);
    }

    #right-panel {
      float: right;
      width: 50%;
      box-sizing: border-box;
      padding: 0 var(--web-kiosk-panel-padding, 64px);
    }

    #hours-details {
      display: grid;
      grid-gap: 10%;
      gap: 10%;
      grid-template-columns: repeat(auto-fit, minmax(300px, 2fr));
      line-height: 56px;
      padding-bottom: 24px;
    }

    #hours-details dd {
      display: flex;
      justify-content: space-between;
      border-top: 1px solid var(--web-kiosk-hours-border-color, #3232643d);
      margin: 0;
      padding: 4px 0;
      font-size: 24px;
      color: #0a1414;
    }

    #hours-details dd:first-of-type {
      border-top: none;
    }

    #hours-details span {
      font-size: 24px;
    }
  `;
}
